import React, { useState } from 'react';
import { FiBell, FiX, FiAlertCircle, FiClock, FiMapPin, FiDollarSign, FiUser } from 'react-icons/fi';
import { Transition } from '@headlessui/react';

const ALERT_TYPES = {
  urgent: { icon: FiAlertCircle, color: 'coral', bg: 'bg-coral-50 dark:bg-coral-900/20', text: 'text-coral-700 dark:text-coral-400' },
  warning: { icon: FiClock, color: 'amber', bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-700 dark:text-amber-400' },
  info: { icon: FiMapPin, color: 'sky', bg: 'bg-sky-50 dark:bg-sky-900/20', text: 'text-sky-700 dark:text-sky-400' },
  success: { icon: FiDollarSign, color: 'emerald', bg: 'bg-emerald-50 dark:bg-emerald-900/20', text: 'text-emerald-700 dark:text-emerald-400' },
  driver: { icon: FiUser, color: 'violet', bg: 'bg-violet-50 dark:bg-violet-900/20', text: 'text-violet-700 dark:text-violet-400' },
};

export default function AlertCenter({ alerts, onDismiss, onDismissAll }) {
  const [isOpen, setIsOpen] = useState(false);
  const unreadCount = alerts.filter(a => !a.dismissed).length;

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2.5 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      >
        <FiBell className="w-5 h-5 text-gray-600 dark:text-gray-300" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-coral-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      <Transition
        show={isOpen}
        as={React.Fragment}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 translate-y-2"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-2"
      >
        <div className="absolute right-0 mt-2 w-96 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 z-50 max-h-[600px] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 dark:text-white">Alerts</h3>
            {unreadCount > 0 && (
              <button
                onClick={onDismissAll}
                className="text-sm text-sky-600 dark:text-sky-400 hover:underline"
              >
                Dismiss all
              </button>
            )}
          </div>

          {/* Alerts List */}
          <div className="flex-1 overflow-y-auto">
            {alerts.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <FiBell className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400">No alerts</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {alerts.map((alert, idx) => {
                  const type = ALERT_TYPES[alert.type] || ALERT_TYPES.info;
                  const Icon = type.icon;
                  
                  return (
                    <div
                      key={idx}
                      className={`px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                        alert.dismissed ? 'opacity-50' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${type.bg}`}>
                          <Icon className={`w-5 h-5 ${type.text}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 dark:text-white text-sm">
                            {alert.title}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {alert.message}
                          </p>
                          {alert.orderId && (
                            <p className="text-xs font-mono text-gray-400 dark:text-gray-500 mt-1">
                              #{alert.orderId}
                            </p>
                          )}
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                            {new Date(alert.timestamp).toLocaleTimeString()}
                          </p>
                        </div>
                        <button
                          onClick={() => onDismiss(idx)}
                          className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-400 transition-colors"
                        >
                          <FiX className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </Transition>
    </div>
  );
}

